import { useEffect, useRef, useCallback } from 'react';

export const useBrowserNotifications = (notifications, onNotificationClick) => {
  const previousCountRef = useRef(0);
  const permissionGranted = useRef(false);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        permissionGranted.current = true;
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          permissionGranted.current = permission === 'granted';
        });
      }
    }
  }, []);

  // Show browser notification for new unread notifications
  useEffect(() => {
    if (!permissionGranted.current || !('Notification' in window)) return;
    
    const unreadNotifications = notifications.filter(n => !n.read);
    const currentCount = unreadNotifications.length;
    
    // Check if there are new notifications
    if (currentCount > previousCountRef.current && previousCountRef.current > 0) {
      // Get the newest notification
      const newestNotification = unreadNotifications[0];
      if (newestNotification) {
        showBrowserNotification(newestNotification, onNotificationClick);
      }
    }
    
    previousCountRef.current = currentCount;
  }, [notifications, onNotificationClick]);

  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      permissionGranted.current = permission === 'granted';
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  return { requestPermission, isSupported: 'Notification' in window };
};

const showBrowserNotification = (notification, onClick) => {
  try {
    const browserNotif = new Notification('WorkStream Pro', {
      body: `${notification.task_title}: ${notification.message}`,
      icon: '/favicon.ico',
      tag: notification.id,
      requireInteraction: false,
    });

    browserNotif.onclick = () => {
      window.focus();
      if (onClick && notification.task_id) {
        onClick(notification.task_id);
      }
      browserNotif.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => browserNotif.close(), 5000);
  } catch (err) {
    console.error('Failed to show browser notification:', err);
  }
};

export default useBrowserNotifications;
